import {ApiClient} from '@hconnect/apiclient'
import {Iso8601} from '@hconnect/common/types'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect} from 'react'

import {AggregatedWorkOrdersByRevisionCodeDto, WorkOrdersQueryKey} from '../../types'

const QueryKeyPrefix = WorkOrdersQueryKey.WorkOrdersByRevisionCode

type WorkOrdersByRevisionCodeParams = {
  plantId: string
  from: Iso8601
  to: Iso8601
  revisionCode?: string
}

export const useWorkOrdersByRevisionCode = <TSelect = AggregatedWorkOrdersByRevisionCodeDto>(
  params: WorkOrdersByRevisionCodeParams,
  options?: Omit<
    UseQueryOptions<AggregatedWorkOrdersByRevisionCodeDto, AxiosError, TSelect>,
    'queryKey' | 'queryFn'
  > & {
    onError?: (error: AxiosError) => void
  }
) => {
  const {plantId, from, to, revisionCode} = params

  const query = useQuery<AggregatedWorkOrdersByRevisionCodeDto, AxiosError, TSelect>({
    queryKey: [QueryKeyPrefix, plantId, from, to, revisionCode],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<AggregatedWorkOrdersByRevisionCodeDto>(
        `/work-orders/${plantId}/work-orders/aggregated-by-revision-code`,
        {
          params: {
            from,
            to,
            revisionCode
          },
          signal
        }
      )

      return response.data
    },
    ...options
  })

  const onError = options?.onError

  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
